import { BookingModuleStoreType } from "PFApp/booking/store";
import { CalendarMetric, CalendarView } from "PFApp/booking/types";
import { BookingsVisibility } from "PFApp/booking/types/bookings_visibility";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { CalendarGranularity } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import { FeatureFlag } from "PFTypes";
import { StateCreator } from "zustand";

import { getCalendarRootStoreSliceProviders } from "./get_calendar_root_store_slice_providers";

type WorkforceSliceCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  BookingModuleStoreType[CalendarRoot.WorkforceProfiles]
>;
type WorkforceSliceActionsCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  BookingModuleStoreType["actions"][CalendarRoot.WorkforceProfiles]
>;
type UseWorkforceStoreStateReturn = {
  createWorkforceStoreSlice: WorkforceSliceCreator;
  createWorkforceStoreSliceActions: WorkforceSliceActionsCreator;
};

export const useWorkforceStoreSlice = (): UseWorkforceStoreStateReturn => {
  const showUtilizationMetrics = useIsFeatureEnabled()(FeatureFlag.ShowUtilization);
  const { createCalendarRootStoreSlice, createCalendarRootStoreSliceActions } =
    getCalendarRootStoreSliceProviders({ calendarRoot: CalendarRoot.WorkforceProfiles });

  const createWorkforceStoreSlice: WorkforceSliceCreator = () => ({
    view: CalendarView.Compressed,
    metric: showUtilizationMetrics ? CalendarMetric.Utilization : CalendarMetric.TotalHours,
    calendarGranularity: CalendarGranularity.Daily,
    availabilitiesByProfile: {},
    bookingsVisibility: BookingsVisibility.All,
    bookingsVisibilityVisibleRowKeys: null,
    ...createCalendarRootStoreSlice()
  });

  const createWorkforceStoreSliceActions: WorkforceSliceActionsCreator = (...args) => {
    const [set, get] = args;
    return {
      setView: (view) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, view } });
      },
      setMetric: (metric) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, metric } });
      },
      setCalendarGranularity: (calendarGranularity) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, calendarGranularity } });
      },
      setAvailabilitiesByProfile: (availabilitiesByProfile) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, availabilitiesByProfile } });
      },
      setBookingsVisibility: (bookingsVisibility) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, bookingsVisibility } });
      },
      setBookingsVisibilityVisibleRowKeys: (bookingsVisibilityVisibleRowKeys) => {
        const workforceSlice = get()[CalendarRoot.WorkforceProfiles];
        set({ [CalendarRoot.WorkforceProfiles]: { ...workforceSlice, bookingsVisibilityVisibleRowKeys } });
      },
      ...createCalendarRootStoreSliceActions(...args)
    };
  };

  return { createWorkforceStoreSlice, createWorkforceStoreSliceActions };
};
